import { useContext, useState } from 'react';
import { useQuery } from 'react-query';
import { SettingsContext } from '../store/SettingsContext';
import { UserContext } from '../store/UserContext';
import useAxios from './useAxios';
import useSubscriber from './useSubscriber';

export interface OnlyFansUserAvatarThumbs {
    c50: string;
    c144: string;
}

export interface OnlyFansUserHeaderSize {
    width: number;
    height: number;
}

export interface OnlyFansUserHeaderThumbs {
    w480: string;
    w760: string;
}

export interface OnlyFansUserListsState {
    id: number;
    type: string;
    name: string;
    hasUser: boolean;
    canAddUser: boolean;
}

export interface OnlyFansUserPromotion {
    id: number;
    message: string;
    rawMessage: string;
    hasRelatedPromo: boolean;
    price: number;
    type: string;
    canClaim: boolean;
    claimsCount: number;
    subscribeCounts: null;
    subscribeDays: number;
    createdAt: Date;
    finishedAt: Date;
}

export interface SubscribedDataSubscribe {
    id: number;
    userId: number;
    subscriberId: number;
    date: Date;
    duration: number;
    startDate: Date;
    expireDate: Date;
    cancelDate?: Date;
    price: number;
    regularPrice: number;
    discount: number;
    earningId: number;
    action: string;
    type: string;
    offerStart?: Date;
    offerEnd?: Date;
    isCurrent: boolean;
}

export interface OnlyFansUserSubscribedByData {
    price: number;
    newPrice: number;
    regularPrice: number;
    subscribePrice: number;
    discountPercent: number;
    discountPeriod: number;
    subscribeAt: Date;
    expiredAt: Date;
    renewedAt: null;
    discountFinishedAt: null;
    discountStartedAt: null;
    status: null;
    isMuted: boolean;
    unsubscribeReason: string;
    duration: string;
    showPostsInFeed: boolean;
    newPostsAfterExpireCount: number;
    subscribes: SubscribedDataSubscribe[];
}

export interface SubscribedOnData {
    price: number;
    newPrice: number;
    regularPrice: number;
    subscribePrice: number;
    discountPercent: number;
    discountPeriod: number;
    subscribeAt: Date;
    expiredAt: Date;
    renewedAt?: Date;
    discountFinishedAt?: Date;
    discountStartedAt?: Date;
    status?: string;
    isMuted: boolean;
    unsubscribeReason: string;
    duration: string;
    messagesSumm: number;
    tipsSumm: number;
    subscribesSumm: number;
    postsSumm: number;
    streamsSumm: number;
    totalSumm: number;
    subscribes: SubscribedDataSubscribe[];
}

export interface OnlyFansUserSubscriptionBundle {
    id: number;
    discount: number;
    duration: number;
    price: number;
    canBuy: boolean;
}

export enum View {
    M = 'm',
}

export interface OnlyFansUser {
    view: View;
    avatar: string;
    avatarThumbs: OnlyFansUserAvatarThumbs;
    header: string;
    headerSize: OnlyFansUserHeaderSize;
    headerThumbs: OnlyFansUserHeaderThumbs;
    id: number;
    name: string;
    username: string;
    canLookStory: boolean;
    canCommentStory: boolean;
    hasNotViewedStory: boolean;
    isVerified: boolean;
    canPayInternal: boolean;
    hasScheduledStream: boolean;
    hasStream: boolean;
    hasStories: boolean;
    tipsEnabled: boolean;
    tipsTextEnabled: boolean;
    tipsMin: number;
    tipsMax: number;
    canEarn: boolean;
    canAddSubscriber: boolean;
    subscribePrice: number;
    subscriptionBundles: OnlyFansUserSubscriptionBundle[];
    displayName?: string;
    notice?: string;
    isPaywallRestriction: boolean;
    unprofitable: boolean;
    listsStates: OnlyFansUserListsState[];
    isMuted: boolean;
    isRestricted: boolean;
    isSuspended?: boolean;
    canRestrict: boolean;
    subscribedBy: boolean | null;
    subscribedByExpire: null;
    subscribedByExpireDate: Date;
    subscribedByAutoprolong: null;
    subscribedIsExpiredNow: boolean;
    currentSubscribePrice: null;
    subscribedOn: boolean;
    subscribedOnExpiredNow: null;
    subscribedOnDuration: null;
    joinDate?: Date;
    isReferrerAllowed?: boolean;
    about?: string;
    rawAbout?: string;
    website?: string;
    wishlist?: unknown;
    location?: string;
    postsCount?: number;
    archivedPostsCount?: number;
    photosCount?: number;
    videosCount?: number;
    audiosCount?: number;
    mediasCount?: number;
    canReport: boolean;
    canReceiveChatMessage: boolean;
    hideChat: boolean;
    lastSeen: Date | null;
    favoritesCount?: number;
    favoritedCount?: number;
    showPostsInFeed?: boolean;
    isPerformer: boolean;
    isRealPerformer: boolean;
    isSpotifyConnected?: boolean;
    subscribersCount?: null;
    hasPinnedPosts?: boolean;
    canChat?: boolean;
    callPrice?: number;
    isPrivateRestriction?: boolean;
    showSubscribersCount?: boolean;
    showMediaCount?: boolean;
    subscribedByData: OnlyFansUserSubscribedByData;
    subscribedOnData: SubscribedOnData;
    canPromotion?: boolean;
    canCreatePromotion?: boolean;
    canCreateTrial?: boolean;
    isAdultContent?: boolean;
    promotions: OnlyFansUserPromotion[];
    canTrialSend: boolean;
    hadEnoughLastPhotos?: boolean;
    isFriend?: boolean;
    isDeleted?: boolean;
    isBlocked: boolean;
}

export interface NameObject {
    firstName?: string | null;
    lastName?: string | null;
}

export interface OnlyFansUserProfile {
    _id: string;
    createdAt?: Date;
    updatedAt?: Date;
    subscriberId: string;
    realName?: string;
    possibleRealName?: NameObject | undefined;
    userId?: number;
    user?: OnlyFansUser;
}

const useInsightsUserProfile = (onlyFansUserId: number | null | undefined) => {
    const userContext = useContext(UserContext);
    const settingsContext = useContext(SettingsContext);
    const { data: subscriber } = useSubscriber();
    const axios = useAxios();

    const [isInsightsUserProfileSaving, setIsInsightsUserProfileSaving] = useState<boolean>(false);

    const fetchData = async (): Promise<OnlyFansUserProfile | null> => {
        const query: string = `${settingsContext.routes.sextforce.base}${subscriber._id}/onlyFansUsers/${onlyFansUserId}/profile`;

        return axios
            .get(query)
            .then(response => response.data)
            .catch(() => {
                return null;
            });
    };

    const query = useQuery(['insightsUserProfile', onlyFansUserId], () => fetchData(), {
        refetchOnWindowFocus: false,
        refetchInterval: 1000 * 60 * 5, // 5 minutes
        enabled:
            userContext.jwtToken && settingsContext.apiKey && userContext.isAdmin && subscriber && subscriber._id && onlyFansUserId
                ? true
                : false,
    });

    const insightsSaveUserRealName = async (realName: string): Promise<OnlyFansUserProfile | null> => {
        const body = {
            realName,
        };

        const query: string = `${settingsContext.routes.sextforce.base}${subscriber._id}/onlyFansUsers/${onlyFansUserId}/profile/realName`;

        setIsInsightsUserProfileSaving(true);

        return axios
            .put(query, body)
            .then(response => response.data)
            .catch(() => {
                return null;
            })
            .finally(() => {
                setIsInsightsUserProfileSaving(false);
            });
    };

    return { ...query, insightsSaveUserRealName, isInsightsUserProfileSaving };
};

export default useInsightsUserProfile;
